import { Injectable } from '@angular/core';
import { Subject } from 'rxjs';
import {Card} from '../model/cards';
import {HotelDtoService} from './hotel-dto-service';
import {PaginationParams} from '../model/pagination';
import {environment} from '../environments/environment';
import {getFirstSentence} from '../functions/getFirstSentence';

@Injectable({
  providedIn: 'root'
})
export class LazyLoadService {
  private currentPage: number = 0;
  private initialPageSize: number = 20;
  private subsequentPageSize: number = 2;
  private allHotelsLoaded: boolean = false;
  private isLoading: boolean = false;
  private isFirstLoad: boolean = true;

  private newCardsSubject = new Subject<Card[]>();
  newCards$ = this.newCardsSubject.asObservable();

  private loadingSubject = new Subject<boolean>();
  loading$ = this.loadingSubject.asObservable();

  private allLoadedSubject = new Subject<boolean>();
  allLoaded$ = this.allLoadedSubject.asObservable();

  constructor(private hotelService: HotelDtoService) {}

  reset() {
    this.currentPage = 0;
    this.allHotelsLoaded = false;
    this.isLoading = false;
    this.isFirstLoad = true;
  }

  loadNextBatch(): void {
    if (this.isLoading || this.allHotelsLoaded) {
      return;
    }
    this.isLoading = true;
    this.loadingSubject.next(true);
    const pageSize = this.isFirstLoad ? this.initialPageSize : this.subsequentPageSize;
    const params = new PaginationParams({
      query: '',
      pageSize: pageSize,
      pageNumber: this.currentPage,
      sort: 'rating:asc',
      cityId: '',
      countryId: '',
      continentId: '',
    });

    this.hotelService.getHotelList(params).subscribe({
      next: (data) => {
        const hotels = (data.content ?? []).map(v => this.mapHotelToCard(v));
        if (hotels.length < pageSize) {
          this.allHotelsLoaded = true;
          this.allLoadedSubject.next(true);
        }
        this.newCardsSubject.next(hotels);
        this.currentPage++;
        this.isLoading = false;
        this.loadingSubject.next(false);
        if (this.isFirstLoad) {
          this.isFirstLoad = false;
        }
      },
      error: (error) => {
        console.error('Failed to load hotels batch:', error);
        this.isLoading = false;
        this.loadingSubject.next(false);
      }
    });
  }

  private mapHotelToCard(v: any): Card {
    const path = v.image ? (environment.s3Prefix + v.image) : '/app/assets/images/hotel.png';
    let link: string | null;
    if (v.city?.country?.continent?.url?.length && v.city?.country?.url) {
      link = v.city?.country?.continent?.url + '/' + v.city?.country?.url + '/' +
        v.city?.url + '/' + v?.url;
    } else {
      link = null;
    }
    let descrRu = '';
    let descrEn = '';
    if (v.descriptionRu !== undefined) {
      descrRu = getFirstSentence(v.descriptionRu);
    }
    if (v.descriptionEn !== undefined) {
      descrEn = getFirstSentence(v.descriptionEn);
    }
    return new Card({
      title: ['', ''],
      text: [v.titleRu, v.titleEn],
      img: path,
      style: v.backgroundStyle?.toString() ?? '',
      link: link,
      rating: v.rating ?? 0,
      anotherButton: true,
      anotherHotelButton: true,
      anotherButtonText: [{text: [descrRu, descrEn]}],
    });
  }
}
